import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Portal from "./pages/Portal";
import JsonFormatter from "./pages/utilities/JsonFormatter";
import HashGenerator from "./pages/utilities/HashGenerator";
import Base64Encoder from "./pages/utilities/Base64Encoder";
import UuidGenerator from "./pages/utilities/UuidGenerator";
import TimestampConverter from "./pages/utilities/TimestampConverter";
import QrCodeGenerator from "./pages/utilities/QrCodeGenerator";
import PasswordGenerator from "./pages/utilities/PasswordGenerator";
import UnitConverter from "./pages/utilities/UnitConverter";
import ChatDashboard from "./pages/chat/ChatDashboard";
import DirectChat from "./pages/chat/DirectChat";
import ChatRooms from "./pages/chat/ChatRooms";
import UserProfile from "./pages/chat/UserProfile";
import TicTacToeLobby from "./pages/game/TicTacToeLobby";
import TicTacToe from "./pages/game/TicTacToe";
import KillMosquito from "./pages/game/KillMosquito";
import DragDragon from "./pages/game/DragDragon";
import CreateArticle from "./pages/blog/CreateArticle";
import MyArticles from "./pages/blog/MyArticles";
import ViewArticle from "./pages/blog/ViewArticle";
import EditArticle from "./pages/blog/EditArticle";
import MovieEntry from "./pages/movies/MovieEntry";
import Showtimes from "./pages/movies/Showtimes";
import SeatSelection from "./pages/movies/SeatSelection";
import BookingStatus from "./pages/movies/BookingStatus";
import AdminDashboard from "./pages/movies/AdminDashboard";
import AdminMovieForm from "./pages/movies/AdminMovieForm";
import AdminSeatMap from "./pages/movies/AdminSeatMap";
import AdminTheatreForm from "./pages/movies/AdminTheatreForm";
import AdminShowForm from "./pages/movies/AdminShowForm";
import AuthCallback from "./pages/AuthCallback";
import { getScopeConfig, isRouteEnabled } from "./config/appScope";
import "./App.css";

function PrivateRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>;
  }
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  return children;
}

function ScopeHome() {
  const scope = getScopeConfig();
  return <Navigate to={scope.home} replace />;
}

function AppRoutes() {
  const showNavbar = isRouteEnabled("home") || isRouteEnabled("dashboard") || isRouteEnabled("portal");

  return (
    <div className="App">
      {showNavbar && <Navbar />}
      <div className="page-content">
        <Routes>
          {isRouteEnabled("portal") && <Route path="/" element={<Portal />} />}

          {isRouteEnabled("home") && <Route path="/" element={<Home />} />}
          {isRouteEnabled("auth") && (
            <>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
            </>
          )}
          {isRouteEnabled("dashboard") && (
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          )}

          {isRouteEnabled("utilities") && (
            <>
              <Route path="/utility/json-formatter" element={<PublicRoute><JsonFormatter /></PublicRoute>} />
              <Route path="/utility/hash-generator" element={<PublicRoute><HashGenerator /></PublicRoute>} />
              <Route path="/utility/base64-encoder" element={<PublicRoute><Base64Encoder /></PublicRoute>} />
              <Route path="/utility/uuid-generator" element={<PublicRoute><UuidGenerator /></PublicRoute>} />
              <Route path="/utility/timestamp-converter" element={<PublicRoute><TimestampConverter /></PublicRoute>} />
              <Route path="/utility/qr-generator" element={<PublicRoute><QrCodeGenerator /></PublicRoute>} />
              <Route path="/utility/password-generator" element={<PublicRoute><PasswordGenerator /></PublicRoute>} />
              <Route path="/utility/unit-converter" element={<PublicRoute><UnitConverter /></PublicRoute>} />
            </>
          )}

          {isRouteEnabled("chat") && (
            <>
              <Route path="/chat" element={<PrivateRoute><ChatDashboard /></PrivateRoute>} />
              <Route path="/chat/direct/:username" element={<PrivateRoute><DirectChat /></PrivateRoute>} />
              <Route path="/chat/rooms" element={<PrivateRoute><ChatRooms /></PrivateRoute>} />
              <Route path="/chat/profile/:username" element={<PrivateRoute><UserProfile /></PrivateRoute>} />
              <Route path="/chat/my-profile" element={<PrivateRoute><UserProfile /></PrivateRoute>} />
            </>
          )}

          {isRouteEnabled("games") && (
            <>
              <Route path="/game/tic-tac-toe" element={<PublicRoute><TicTacToeLobby /></PublicRoute>} />
              <Route path="/game/tic-tac-toe/:gameId" element={<PublicRoute><TicTacToe /></PublicRoute>} />
              <Route path="/game/mosquito" element={<PublicRoute><KillMosquito /></PublicRoute>} />
              <Route path="/game/dragon" element={<PublicRoute><DragDragon /></PublicRoute>} />
            </>
          )}

          {isRouteEnabled("blog") && (
            <>
              <Route path="/blog/create" element={<PrivateRoute><CreateArticle /></PrivateRoute>} />
              <Route path="/blog/my-articles" element={<PrivateRoute><MyArticles /></PrivateRoute>} />
              <Route path="/blog/edit/:articleId" element={<PrivateRoute><EditArticle /></PrivateRoute>} />
              <Route path="/blog/:slug" element={<ViewArticle />} />
            </>
          )}

          {isRouteEnabled("movies") && (
            <>
              <Route path="/movies" element={<MovieEntry />} />
              <Route path="/movies/:movieId/shows" element={<MovieEntry><Showtimes /></MovieEntry>} />
              <Route path="/movies/:movieId/shows/:showId/seats" element={<MovieEntry><SeatSelection /></MovieEntry>} />
              <Route path="/bookings/:bookingId" element={<MovieEntry><BookingStatus /></MovieEntry>} />
              <Route path="/movies/admin" element={<MovieEntry><AdminDashboard /></MovieEntry>} />
              <Route path="/movies/admin/movies" element={<MovieEntry><AdminMovieForm /></MovieEntry>} />
              <Route path="/movies/admin/theatres" element={<MovieEntry><AdminTheatreForm /></MovieEntry>} />
              <Route path="/movies/admin/shows" element={<MovieEntry><AdminShowForm /></MovieEntry>} />
              <Route path="/movies/admin/shows/:showId/seats" element={<MovieEntry><AdminSeatMap /></MovieEntry>} />
            </>
          )}

          <Route path="*" element={<ScopeHome />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
